/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-no-bind */

"use client";

import { ColletteButton } from "@collette-travel/collette-common-controls";
import CollectionsIcon from "@mui/icons-material/Collections";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { PlayCircleOutline } from "@mui/icons-material";
import { getWistiaFirstFrameBase64 } from "@/utils/wistia-thumbnail-fetcher";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";
import TourMediaGalleryModal from "./tour-media-gallery-modal";
import TourMediaGallerySkeleton from "./tour-media-gallery-skeleton";

// Define the type for a single media item
interface MediaItem {
  videoId?: string | null;
  url: string;
  id: string;
  altTag: string;
  thumbnail?: string;
}

// Define the props for the TourMediaGallery component
interface TourMediaGalleryProps {
  tourGalleryData: MediaItem[];
  isLoading: boolean;
}

interface GalleryState {
  show: boolean;
  index: number;
}

const TourMediaGallery: React.FC<TourMediaGalleryProps> = ({ tourGalleryData, isLoading }) => {
  const [gallery, setGallery] = useState<MediaItem[]>([]);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [showGallery, setShowGallery] = useState<GalleryState>({
    show: false,
    index: 0
  });

  const handleGallery = (val: number) => setShowGallery({ show: true, index: val });

  useEffect(() => {
    const processGalleryData = async () => {
      if (!tourGalleryData) return;

      const processedGallery = await Promise.all(
        tourGalleryData.map(async (item) => {
          if (item.videoId) {
            const thumbnail = await getWistiaFirstFrameBase64(
              item.videoId,
              item.url,
              "1920x1080",
              0
            );
            return {
              ...item,
              thumbnail: thumbnail?.base64Image || item.url
            };
          }
          return item;
        })
      );

      setGallery(processedGallery);
    };

    processGalleryData();
  }, [tourGalleryData]);

  useScrollLock(showGallery.show);

  // Optimized MediaBox component using Next.js Image for better LCP
  const MediaBox = ({
    item,
    index,
    width,
    aspectRatio,
    borderRadius,
    showButton = false
  }: {
    item: MediaItem;
    index: number;
    width: any;
    aspectRatio: string;
    borderRadius: any;
    showButton?: boolean;
  }) => (
    <Box
      onClick={() => handleGallery(index)}
      data-testid={`tour-media-gallery-item-${index}`}
      data-track="click"
      data-parent-container-type="tour-media-gallery"
      sx={{
        width,
        aspectRatio,
        borderRadius,
        position: "relative",
        overflow: "hidden",
        bgcolor: "grey.500",
        cursor: "pointer"
      }}
    >
      <Image
        src={`${item?.thumbnail || item?.url}`}
        alt={`${item?.altTag || "Tour Media"}`}
        fill
        style={{ objectFit: "cover" }}
        priority
        quality={85}
        sizes="(max-width: 768px) 350px, (max-width: 1024px) 790px, (min-width: 1025px) 900px"
      />
      {/* Play Icon - Centered */}
      {item?.videoId && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1
          }}
        >
          <PlayCircleOutline
            sx={{
              color: "common.white",
              fontSize: { xs: "48px", md: "64px" },
              filter: `drop-shadow(${theme.shadows[7]})`
            }}
          />
        </Box>
      )}

      {/* View Gallery Button */}
      {showButton && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            padding: "21px",
            zIndex: 2
          }}
        >
          <ColletteButton
            startIcon={<CollectionsIcon fontSize="small" />}
            variant="outlined"
            size="large"
            label="View Gallery"
            testId="view-gallery-button"
            data-track="click"
            onClick={(e) => {
              e.stopPropagation();
              handleGallery(0);
            }}
            sx={{
              backgroundColor: "background.default"
            }}
          />
        </Box>
      )}
    </Box>
  );

  // Show skeleton while loading or when data is not available
  if (isLoading || !tourGalleryData) {
    return <TourMediaGallerySkeleton />;
  }

  // Don't render section if no gallery data is available
  if (!gallery || gallery.length === 0) {
    return <TourMediaGallerySkeleton />;
  }

  // Main Component render

  return (
    <Stack
      data-track="visibility"
      data-testid="tour-media-gallery"
      id="tour-media-gallery"
      data-module-name="tour-media-gallery"
      data-ranking-algorithm="manual"
      data-module-ranking-position="3"
      component="section"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      gap={2}
      sx={{
        borderRadius: "20px",
        marginTop: { xs: "24px", md: "40px" }
      }}
    >
      {/* Main Video/Image Box */}
      <MediaBox
        item={gallery[0]}
        index={0}
        width={{ xs: "100%", lg: "555px" }}
        aspectRatio="555 / 312"
        borderRadius={{
          borderTopLeftRadius: "20px",
          borderBottomLeftRadius: "20px",
          borderTopRightRadius: isDesktop ? 0 : "20px",
          borderBottomRightRadius: isDesktop ? 0 : "20px"
        }}
        showButton={!isDesktop}
      />

      {/* Desktop: First Additional Image */}
      {isDesktop && gallery[1] && (
        <MediaBox
          item={gallery[1]}
          index={1}
          width={352}
          aspectRatio="352 / 312"
          borderRadius={0}
        />
      )}

      {/* Desktop: Second Additional Image with Button */}
      {isDesktop && gallery[2] && (
        <MediaBox
          item={gallery[2]}
          index={2}
          width={352}
          aspectRatio="352 / 312"
          borderRadius={{
            borderTopRightRadius: "20px",
            borderBottomRightRadius: "20px"
          }}
          showButton
        />
      )}

      {/* Gallery Modal */}
      {showGallery.show && (
        <TourMediaGalleryModal
          showGallery={showGallery}
          setShowGallery={setShowGallery}
          mediaGallery={gallery}
        />
      )}
    </Stack>
  );
};

export default TourMediaGallery;
